import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../auth/AuthContext.js";
import { api, ApiError } from "../../../api/client.js";
import type { ImageBatchView, ImageContentMode, ImageModeConfig, ImagePersonaOption, ImageSourceUpload } from "../../../api/types.js";
import {
  CONTENT_MODE_LABEL,
  NO_REQUESTED_CHANGES,
  OPERATOR_ACTION_ROLE,
  buildRequestedChangesBody,
  clampRequestedCount,
  effectiveImageRole,
  estimatedGenerationCostUsd,
  formatUsd,
  requestedChangesProblem,
  roleAtLeast,
  type RequestedChangeSelection,
} from "./image-ui.js";

/** The three modes, in display order. There is NO default: the user must choose one explicitly before GENERATE is enabled. */
const CONTENT_MODES: readonly ImageContentMode[] = ["sfw", "spicy", "nsfw"];

const ACCESS_MESSAGE: Record<string, string> = {
  product_license_inactive: "This workspace does not currently have an active product license, so new images cannot be generated right now. Existing results remain available.",
  provider_spend_limit_reached: "This workspace has reached its monthly provider spend limit, so new images cannot be generated until the limit resets or is raised. Existing results remain available.",
};

/** Bounded lookup delays (ms) used only after the Generate POST failed at the NETWORK level — never a second POST. */
const RECONCILE_DELAYS_MS = [1000, 2000, 4000];

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function formatBytes(bytes: number): string {
  return bytes < 1024 * 1024 ? `${Math.round(bytes / 1024)} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * IMAGE mode's create form: ONE source image, ONE persona, 1–15 images,
 * "Estimated generation cost", and one Generate action. Generate creates a
 * durable batch and returns immediately; the work continues in the backend,
 * so this page can be closed at any time.
 */
export function ImageCreateTab({ tenantId }: { readonly tenantId: string }) {
  const { csrfToken, user, memberships } = useAuth();
  const navigate = useNavigate();
  const role = effectiveImageRole({ isPlatformOwner: user?.isPlatformOwner === true, memberships, tenantId });
  const canOperate = roleAtLeast(role, OPERATOR_ACTION_ROLE);

  const [config, setConfig] = useState<ImageModeConfig | null>(null);
  const [personas, setPersonas] = useState<ImagePersonaOption[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [uploading, setUploading] = useState(false);
  const [source, setSource] = useState<ImageSourceUpload | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [selectedPersonaId, setSelectedPersonaId] = useState<string | null>(null);
  const [count, setCount] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // No mode is ever pre-selected, inferred from the image, or classified automatically.
  const [contentMode, setContentMode] = useState<ImageContentMode | null>(null);
  const [changes, setChanges] = useState<RequestedChangeSelection>(NO_REQUESTED_CHANGES);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    Promise.all([api.get<ImageModeConfig>(`/images/${encodeURIComponent(tenantId)}/config`), api.get<{ personas: ImagePersonaOption[] }>(`/images/${encodeURIComponent(tenantId)}/personas`)])
      .then(([configResult, personaResult]) => {
        if (cancelled) return;
        setConfig(configResult);
        setPersonas(personaResult.personas);
        const ready = personaResult.personas;
        setSelectedPersonaId((current) => current ?? (ready.length === 1 ? ready[0]!.personaId : (ready.find((p) => p.isDefault)?.personaId ?? null)));
      })
      .catch((error) => {
        if (!cancelled) setLoadError(error instanceof ApiError ? error.message : "Failed to load Image mode options.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  if (loading) return <div className="empty-state">Loading…</div>;
  if (loadError !== null || config === null) return <div className="error-banner">{loadError ?? "Failed to load Image mode options."}</div>;

  // The server already returns only this user's Generate-ready personas.
  const readyPersonas = personas;
  const selectedPersona = readyPersonas.find((p) => p.personaId === selectedPersonaId) ?? null;
  // The per-image estimate follows the chosen mode's provider (SFW: Phota; SPICY / NSFW: SpicyAPI WAN 2.7).
  const pricePerImage = contentMode === null || contentMode === "sfw" ? config.estimatedGenerationPricePerImageUsd : (config.spicyGenerationPricePerImageUsd ?? config.estimatedGenerationPricePerImageUsd);
  const estimate = estimatedGenerationCostUsd(count, pricePerImage);
  const pendantPresets = config.requestedChangeOptions?.pendantPresets ?? [];
  const glassesPresets = config.requestedChangeOptions?.glassesPresets ?? [];

  const missing: string[] = [];
  if (contentMode === null) missing.push("Choose a content mode: SFW, SPICY or NSFW");
  const changesProblem = requestedChangesProblem(changes);
  if (changesProblem !== null) missing.push(changesProblem);
  if (source === null) missing.push("Upload a source image");
  if (readyPersonas.length === 0) missing.push("No Persona is ready yet — go to the PERSONAS tab to upload your model");
  else if (selectedPersona === null) missing.push("Select a Persona");
  if (!config.access.allowed) missing.push(ACCESS_MESSAGE[config.access.reason ?? ""] ?? "Image generation is not available for this workspace right now.");
  if (!canOperate) missing.push("Your role can view Image results but cannot upload or generate — OPERATOR or higher is required.");
  const canSubmit = missing.length === 0 && !submitting && !reconciling;

  async function handleFileChosen(file: File | null): Promise<void> {
    if (file === null || config === null) return;
    setUploadError(null);
    if (!config.acceptedContentTypes.includes(file.type)) {
      setUploadError("Only JPEG and PNG images are supported.");
      return;
    }
    if (file.size > config.maxSourceBytes) {
      setUploadError(`That image is ${formatBytes(file.size)} — the limit is ${formatBytes(config.maxSourceBytes)}.`);
      return;
    }
    setUploading(true);
    try {
      setSource(await api.uploadBinary<ImageSourceUpload>(`/images/${encodeURIComponent(tenantId)}/source-images`, csrfToken, file));
    } catch (error) {
      setUploadError(error instanceof ApiError ? error.message : "Upload failed.");
    } finally {
      setUploading(false);
    }
  }

  async function reconcile(clientRequestId: string): Promise<void> {
    setReconciling(true);
    for (let attempt = 0; attempt <= RECONCILE_DELAYS_MS.length; attempt += 1) {
      if (attempt > 0) await sleep(RECONCILE_DELAYS_MS[attempt - 1]!);
      try {
        const result = await api.get<{ batch: ImageBatchView }>(`/images/${encodeURIComponent(tenantId)}/batches/by-client-request-id/${encodeURIComponent(clientRequestId)}`);
        navigate(`/generate/create/image/batches/${result.batch.id}`);
        return;
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) {
          setSubmitError("The request never reached the server and nothing was started. Please try again.");
          break;
        }
        if (attempt === RECONCILE_DELAYS_MS.length) {
          setSubmitError("Couldn't confirm whether your request went through. Check your Image batches below before trying again — do not resubmit blindly.");
        }
      }
    }
    setReconciling(false);
    setSubmitting(false);
  }

  async function handleGenerate(): Promise<void> {
    if (source === null || selectedPersona === null || contentMode === null) return;
    setSubmitting(true);
    setSubmitError(null);
    const clientRequestId = `image-ui-${crypto.randomUUID()}`;
    // ONE Generate click: the mode, the persona, the source, the count and every Requested Change are decided BEFORE it — nothing is asked later.
    const requestedChanges = buildRequestedChangesBody(changes);
    try {
      const result = await api.post<{ batch: ImageBatchView }>(`/images/${encodeURIComponent(tenantId)}/batches`, csrfToken, {
        sourceImageId: source.id,
        personaId: selectedPersona.personaId,
        requestedCount: count,
        clientRequestId,
        contentMode,
        // Only what was selected is ever sent, so an unselected change can never be executed.
        ...(requestedChanges !== null ? { requestedChanges } : {}),
      });
      navigate(`/generate/create/image/batches/${result.batch.id}`);
    } catch (error) {
      if (error instanceof ApiError) {
        setSubmitError(error.message);
        setSubmitting(false);
        return;
      }
      await reconcile(clientRequestId);
    }
  }

  return (
    <div>
      {submitError && <div className="error-banner">{submitError}</div>}

      <div className="card">
        <h3>Content mode</h3>
        <div role="radiogroup" aria-label="Content mode" data-testid="image-content-mode" style={{ display: "flex", gap: 8 }}>
          {CONTENT_MODES.map((mode) => (
            <button
              key={mode}
              type="button"
              role="radio"
              aria-checked={contentMode === mode}
              className={contentMode === mode ? "primary" : undefined}
              disabled={!canOperate}
              data-testid={`image-content-mode-${mode}`}
              onClick={() => setContentMode(mode)}
            >
              {CONTENT_MODE_LABEL[mode]}
            </button>
          ))}
        </div>
        <p className="muted">Choose one before you Generate — no mode is selected for you. The mode is fixed for the whole request.</p>
      </div>

      <div className="grid grid-2">
        <div className="card">
          <h3>Source Image</h3>
          {source === null ? (
            <div>
              <div className="field">
                <label htmlFor="image-source-file">Upload one source image</label>
                <input
                  id="image-source-file"
                  type="file"
                  accept={config.acceptedContentTypes.join(",")}
                  disabled={uploading || !canOperate}
                  onChange={(event) => void handleFileChosen(event.target.files?.[0] ?? null)}
                />
              </div>
              <p className="muted">JPEG or PNG, up to {formatBytes(config.maxSourceBytes)}. The image defines the scenario; your persona replaces the person in it.</p>
              {uploading && <p className="muted">Uploading…</p>}
              {uploadError && <div className="error-banner">{uploadError}</div>}
            </div>
          ) : (
            <div>
              <img className="image-source-preview" src={source.previewUrl} alt="Selected source" data-testid="image-source-preview" />
              <p className="muted">
                {source.width}×{source.height} — {formatBytes(source.sizeBytes)}
              </p>
              <button type="button" onClick={() => setSource(null)}>
                Change source image
              </button>
            </div>
          )}
        </div>

        <div className="card">
          <h3>Persona</h3>
          {readyPersonas.length === 0 && <p className="muted">You have no ready personas yet.</p>}
          <div className="grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            {readyPersonas.map((persona) => (
              <div
                key={persona.personaId}
                role="button"
                tabIndex={0}
                aria-pressed={selectedPersonaId === persona.personaId}
                className="card"
                style={{ cursor: "pointer", borderColor: selectedPersonaId === persona.personaId ? "var(--accent)" : undefined, marginBottom: 0 }}
                onClick={() => setSelectedPersonaId(persona.personaId)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") setSelectedPersonaId(persona.personaId);
                }}
              >
                <strong>{persona.displayName}</strong>
                {persona.isDefault && <span className="badge status-approved" style={{ marginLeft: 6 }}>Default</span>}
                <div className="muted">v{persona.identityVersion}</div>
              </div>
            ))}
          </div>

          <h3 style={{ marginTop: 20 }}>Number of images</h3>
          <div className="count-control">
            <button type="button" aria-label="Decrease number of images" disabled={count <= config.minRequestedCount} onClick={() => setCount((c) => clampRequestedCount(c - 1, config.minRequestedCount, config.maxRequestedCount))}>
              −
            </button>
            <input
              id="image-count"
              aria-label="Number of images"
              type="number"
              min={config.minRequestedCount}
              max={config.maxRequestedCount}
              value={count}
              onChange={(event) => setCount(clampRequestedCount(Number(event.target.value), config.minRequestedCount, config.maxRequestedCount))}
            />
            <button type="button" aria-label="Increase number of images" disabled={count >= config.maxRequestedCount} onClick={() => setCount((c) => clampRequestedCount(c + 1, config.minRequestedCount, config.maxRequestedCount))}>
              +
            </button>
          </div>
          <input
            type="range"
            aria-label="Number of images slider"
            min={config.minRequestedCount}
            max={config.maxRequestedCount}
            value={count}
            onChange={(event) => setCount(clampRequestedCount(Number(event.target.value), config.minRequestedCount, config.maxRequestedCount))}
            style={{ width: "100%" }}
          />
          <p data-testid="image-count-display">
            <strong>{count}</strong> image{count === 1 ? "" : "s"} will be requested ({config.minRequestedCount}–{config.maxRequestedCount}).
          </p>
        </div>
      </div>

      <div className="card" data-testid="image-requested-changes">
        <h3>Requested changes</h3>
        <p className="muted">Optional. Whatever you tick here is applied automatically to every image after it is generated — nothing else changes, and you are not asked again.</p>

        <div className="field">
          <label>
            <input
              type="checkbox"
              checked={changes.clothingColorEnabled}
              disabled={!canOperate}
              onChange={(event) => setChanges((c) => ({ ...c, clothingColorEnabled: event.target.checked }))}
            />{" "}
            Change clothing color
          </label>
          {changes.clothingColorEnabled && (
            <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
              <input aria-label="Garment (optional)" placeholder="Garment, e.g. bikini top (optional)" value={changes.clothingGarment} onChange={(event) => setChanges((c) => ({ ...c, clothingGarment: event.target.value }))} />
              <input aria-label="New color" placeholder="New color, e.g. red" value={changes.clothingColor} onChange={(event) => setChanges((c) => ({ ...c, clothingColor: event.target.value }))} />
            </div>
          )}
        </div>

        <div className="field">
          <label>
            <input
              type="checkbox"
              checked={changes.pendantEnabled}
              disabled={!canOperate}
              onChange={(event) => setChanges((c) => ({ ...c, pendantEnabled: event.target.checked, pendantPrompt: event.target.checked && c.pendantPrompt === "" ? (pendantPresets[0]?.prompt ?? "") : c.pendantPrompt }))}
            />{" "}
            Add pendant
          </label>
          {changes.pendantEnabled && pendantPresets.length > 0 && (
            <select aria-label="Pendant" value={changes.pendantPrompt} onChange={(event) => setChanges((c) => ({ ...c, pendantPrompt: event.target.value }))} style={{ marginTop: 6 }}>
              {pendantPresets.map((preset) => (
                <option key={preset.id} value={preset.prompt}>
                  {preset.label}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="field">
          <label>
            <input
              type="checkbox"
              checked={changes.glassesEnabled}
              disabled={!canOperate}
              onChange={(event) => setChanges((c) => ({ ...c, glassesEnabled: event.target.checked, glassesPrompt: event.target.checked && c.glassesPrompt === "" ? (glassesPresets[0]?.prompt ?? "") : c.glassesPrompt }))}
            />{" "}
            Add glasses
          </label>
          {changes.glassesEnabled && glassesPresets.length > 1 && (
            <select aria-label="Glasses" value={changes.glassesPrompt} onChange={(event) => setChanges((c) => ({ ...c, glassesPrompt: event.target.value }))} style={{ marginTop: 6 }}>
              {glassesPresets.map((preset) => (
                <option key={preset.id} value={preset.prompt}>
                  {preset.label}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      <div className="card">
        <h3>Estimated generation cost</h3>
        <p data-testid="image-estimated-cost">
          <strong>{formatUsd(estimate)}</strong> ({count} × {formatUsd(pricePerImage)})
        </p>
        {contentMode === null && <p className="muted">Shown at the SFW price until you choose a content mode — SPICY and NSFW are priced per image by their own provider.</p>}
        <p className="muted">
          This is the initial generation estimate only — not a total. Identity checks, similarity checks and any retries add further cost; the real tracked cost is shown after you Generate.
        </p>
      </div>

      <div className="card">
        <button className="primary" disabled={!canSubmit} onClick={() => void handleGenerate()}>
          {reconciling ? "Confirming your request…" : submitting ? "Starting…" : "Generate"}
        </button>
        {missing.length > 0 && (
          <ul className="muted" style={{ marginTop: 10 }} data-testid="image-missing-requirements">
            {missing.map((requirement) => (
              <li key={requirement}>{requirement}</li>
            ))}
          </ul>
        )}
        {reconciling && <p className="muted">We lost the connection while starting your request — checking whether it went through. We will not submit it again.</p>}
        <p className="muted" style={{ marginTop: 8 }}>
          Generation continues in the cloud after you click Generate — you can close this page.
        </p>
      </div>
    </div>
  );
}
